/**
 * GYM-1072.3 — Soft re-consent banner.
 * Only shown on the /consent page when the member's accepted policy version is outdated.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, X } from "lucide-react";
import { useCurrentMember } from "@/pages/member/ui/useCurrentMember";
import { pillButtonClass } from "./ui/styles";
import { memberGetConsentState, memberGetCurrentPolicyDocuments } from "@/lib/api/member";

export function ReConsentBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const member = useCurrentMember();
  const memberId = member?.member_id ?? "";
  const [dismissed, setDismissed] = useState(false);

  const onConsentPage = pathname.includes("/consent");

  const { data: state = [] } = useQuery({
    queryKey: ["member-consent-state", memberId],
    queryFn: () => memberGetConsentState(memberId),
    enabled: onConsentPage && Boolean(memberId) && !dismissed,
    staleTime: 60_000,
  });

  const { data: docs = [] } = useQuery({
    queryKey: ["consent-policy-docs"],
    queryFn: memberGetCurrentPolicyDocuments,
    enabled: onConsentPage && Boolean(memberId) && !dismissed,
    staleTime: 5 * 60_000,
  });

  if (!onConsentPage || dismissed || !memberId) return null;

  const needsReconsent = (["terms", "privacy"] as const).some((kind) => {
    const currentDoc = docs.find((d) => d.kind === kind);
    const acceptedRow = state.find((r) => r.consent_type === kind);
    if (!currentDoc) return false;
    if (!acceptedRow?.policy_version_detail) return true;
    return acceptedRow.policy_version_detail.version < currentDoc.version;
  });

  if (!needsReconsent) return null;

  return (
    <div role="status" aria-live="polite" className="flex items-start gap-3 rounded-2xl bg-warning-500/10 px-4 py-3 text-sm">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink-900">{t("memberPortal.consent.reconsent.title")}</p>
        <p className="mt-0.5 text-ink-500">{t("memberPortal.consent.reconsent.desc")}</p>
        <button type="button" className={pillButtonClass("secondary", "sm") + " mt-3"} onClick={() => navigate("/member/consent")}>
          {t("memberPortal.consent.reconsent.cta")}
        </button>
      </div>
      <button
        type="button"
        aria-label={t("common.dismiss")}
        onClick={() => setDismissed(true)}
        className="-me-2 -mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink-500 hover:bg-ink-900/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
